import { useCallback, useState } from 'react';
import { toast } from '../../../components';
import { useCreateSubscriptionMutation } from '../../../redux/services/subscriptions-service';
import { pendoUpdate } from '../../Pendo';
import { useSubscriptionParams } from './useSubscriptionParams';
import { useValidateBillingCode } from './useValidateBillingCode';
import { useOktaSignIn } from './useOktaSignIn';
import { SUBSCRIPTION_TOAST_IDS } from '../constants';

interface SubscriptionFormData {
    UserName: string;
    FirstName?: string;
    LastName?: string;
    BillingAssociationCode?: string;
    AccountId?: string;
}

interface NextStepData {
    UserName: string;
    BillingAssociationCode?: string;
}

/**
 * Custom hook to handle subscription creation flow
 * Encapsulates validation, API calls, and redirect logic
 *
 * @returns Object with submit handler, loading state, and next step data
 */
export const useSubscriptionCreate = () => {
    const { marketplaceToken, allParams, listing } = useSubscriptionParams();
    const { validateCode } = useValidateBillingCode();
    const { signInWithState } = useOktaSignIn();
    const [createSubscription, { isLoading: apiLoading }] = useCreateSubscriptionMutation();
    const [nextStep, setNextStep] = useState<NextStepData | undefined>();
    const [redirecting, setRedirecting] = useState(false);
    const isLoading = apiLoading || redirecting;

    /**
     * Creates a subscription with the provided form data
     */
    const callSubscriptionCreate = useCallback(async (params: SubscriptionFormData) => {
        const { FirstName, LastName, UserName, BillingAssociationCode } = params;
        const subscriptionPayload = {
            UserName,
            RegistrationToken: marketplaceToken,
            ...(FirstName && { FirstName }),
            ...(LastName && { LastName }),
            ...(BillingAssociationCode && { Bac: BillingAssociationCode })
        };

        try {
            const result = await createSubscription({ payload: subscriptionPayload }).unwrap();
            if (result) {
                pendoUpdate({ ...params });

                // If email already has Panwid, redirect to login immediately
                if (result?.PanwidEmailSent === false) {
                    setRedirecting(true);
                    signInWithState(UserName, {
                        ...(BillingAssociationCode && { BillingAssociationCode }),
                        ...(FirstName && { FirstName }),
                        ...(LastName && { LastName }),
                    });
                } else {
                    // Show activation message (user needs to check email)
                    setNextStep({
                        UserName,
                        BillingAssociationCode
                    });
                }
            }
        } catch (error: any) {
            toast.error(`${error?.code}: ${error?.error}`, {
                toastId: SUBSCRIPTION_TOAST_IDS.CREATE_FIREWALL
            });
        }
    }, [marketplaceToken, allParams, createSubscription, signInWithState]);

    /**
     * Main submit handler for the subscription form
     * Validates billing code if present, then creates subscription
     */
    const handleSubmit = useCallback(async (params: SubscriptionFormData, hasCode?: string) => {
        // Check if registration token exists
        if (!marketplaceToken) {
            toast.error(
                "Registration Token Missing. Please go to AWS Marketplace and follow the instructions to set up your account!"
            );
            return;
        }

        // If user has billing code, validate it first
        if (hasCode === "Yes" && params?.BillingAssociationCode) {
            const isValid = await validateCode(
                params.UserName,
                params.BillingAssociationCode,
                params.AccountId || '',
                listing
            );

            if (isValid) {
                await callSubscriptionCreate(params);
            }
        } else {
            // No billing code - create subscription without validation
            await callSubscriptionCreate(params);
        }
    }, [marketplaceToken, validateCode, callSubscriptionCreate, listing]);

    return {
        handleSubmit,
        isLoading,
        nextStep
    };
};
